import { cx } from '@7f-ui'
import { Divider } from '@7f-ui/components/divider/divider'
import { useExperienceCancelationDeadline } from '@feat/feat-experience/booking-detail'
import { CalendarCheckIcon } from '@phosphor-icons/react'
import { NON_CANCELABLE } from '@utils/utils-config'
import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'
import { useEffect, useState } from 'react'

type AppExperienceCancelationProps = {
  variant?: 'inline' | 'outlined' | 'success'
  cancelationHours: number
  isTicket?: boolean
}

const DELAY_DURATION = 5000
const SHOW_DURATION = 5000

export function AppExperienceCancelation({
  cancelationHours,
  variant = 'inline',
  isTicket,
}: AppExperienceCancelationProps) {
  const formattedCancelationDeadline = useExperienceCancelationDeadline(cancelationHours)

  if (cancelationHours === NON_CANCELABLE) return null

  return (
    <div
      className={cx('flex items-center gap-4', {
        'rounded-2xl border border-primary/15 bg-white p-6': variant === 'outlined',
        'rounded-2xl bg-success-bg p-6': variant === 'success',
        'border-primary/15 bg-white': variant === 'inline',
      })}
    >
      <CalendarCheckIcon className="size-6 text-success" />
      <div className="flex flex-col">
        <p
          className={cx('font-body-small font-semibold text-success', {
            uppercase: variant === 'inline',
          })}
        >
          Kostenlose Stornierung
        </p>
        <p
          className={cx('font-body-small', {
            'font-base-copy text-secondary-text': variant === 'success',
          })}
        >
          {`bis ${formattedCancelationDeadline ?? ''} ${isTicket ? 'vorher' : 'vor Beginn'}`}
        </p>
      </div>
    </div>
  )
}

const animationVariants = {
  initial: {
    opacity: 0,
    y: 30,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
  exit: {
    opacity: 0,
    y: 30,
    scale: 0.95,
  },
}

export function AppAnimatedExperienceCancelation({
  cancelationHours,
  isTicket,
}: {
  cancelationHours: number
  isTicket?: boolean
}) {
  const [isVisible, setIsVisible] = useState(false)
  const formattedCancelationDeadline = useExperienceCancelationDeadline(cancelationHours)

  useEffect(() => {
    const showTimer = setTimeout(() => {
      setIsVisible(true)
    }, DELAY_DURATION)

    const hideTimer = setTimeout(() => {
      setIsVisible(false)
    }, DELAY_DURATION + SHOW_DURATION)

    return () => {
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
    }
  }, [])

  if (cancelationHours === NON_CANCELABLE) return null

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <m.div
            initial="initial"
            animate="animate"
            exit="exit"
            variants={animationVariants}
            transition={{
              duration: 0.6,
              ease: [0.25, 0.46, 0.45, 0.94],
              opacity: { duration: 0.4 },
              y: { duration: 0.6 },
              scale: { duration: 0.5 },
            }}
            className="flex gap-1 bg-white px-6 py-2"
          >
            <span className="font-base-copy font-semibold text-success">
              Kostenlose Stornierung
            </span>
            <span className="font-base-copy">
              {`bis ${formattedCancelationDeadline ?? ''} ${isTicket ? 'vorher' : 'vor Beginn'}`}
            </span>
          </m.div>
          <Divider />
        </>
      )}
    </AnimatePresence>
  )
}
